import axiosNest from '../../api/axiosNest';
import { buildListUrl } from '../dashboard/listUrl';

/**
 * Pulpity FTL — `GET /ftl-dashboards/planner` i `GET /ftl-dashboards/team-leader`
 * w API (makieta back office v2, fala A, A4; przełączniki `plannerDashboard`
 * i `teamLeaderDashboard`, 20.10.2026). API oddaje przy każdej liczbie FILTR
 * ekranu, którym ją policzyło, i sam ekran (`target`) — kafelek prowadzi tam
 * z dokładnie tym filtrem, więc liczba i lista się zgadzają.
 */

/** Przełączniki — klucze z rejestru API (dane, nie przemianowujemy). */
export const FEATURE_PLANNER_DASHBOARD = 'plannerDashboard';
export const FEATURE_TEAM_LEADER_DASHBOARD = 'teamLeaderDashboard';
/** Bilans floty, postęp planistów, deficyt i nadwyżka (24.10.2026, S-24.10-08). */
export const FEATURE_TEAM_LEADER_BALANCE = 'teamLeaderBalance';

/** Zasoby (ścieżki) ekranów pulpitów w `<Admin>`. */
export const PLANNER_DASHBOARD_RESOURCE = 'ftl-planner-dashboard';
export const TEAM_LEADER_DASHBOARD_RESOURCE = 'ftl-team-leader-dashboard';

/**
 * Role pulpitów FTL. Ekranem startowym pulpit jest tylko dla planisty FTL —
 * planista główny zaczyna od pulpitu ogólnego, pulpit Team Leadera ma w menu
 * (decyzja użytkownika 20.10.2026).
 */
export const ROLE_PLANNER_FTL = 'PLANNER_FTL';
export const ROLE_FTL_MASTER = 'FTL_MASTER';

export type DashboardTarget = 'orders' | 'fleet' | 'notifications';

export interface DashboardTile {
  key: string;
  group: string;
  target: DashboardTarget;
  filter: Record<string, unknown>;
  count: number;
}

export interface PlannerDashboardData {
  tiles: DashboardTile[];
  caretaker: string | null;
  vehicleCount: number | null;
  regions: string[];
  horizonDays: number;
  regionPostalDigits: number;
  /**
   * Wyjątki krajów (21.10.2026, `regionDigitsByCountry`): kraj ISO → cyfry
   * kodu. Brak = jedna liczba dla wszystkich krajów.
   */
  regionPostalDigitsByCountry?: Record<string, number>;
  computedAt: string;
}

export interface TeamLeaderDashboardData {
  tiles: DashboardTile[];
  horizonDays: number;
  computedAt: string;
}

/** Adres harmonogramu floty z filtrem kafelka (`FleetSchedule` czyta go z adresu). */
export function fleetScheduleUrl(filter: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const key of ['caretaker', 'show']) {
    const v = filter[key];
    if (typeof v === 'string' && v !== '') params.set(key, v);
  }
  const query = params.toString();
  return query ? `/fleet-schedule?${query}` : '/fleet-schedule';
}

/** Adres strony powiadomień z filtrem kafelka (`NotificationHistory` czyta go z adresu). */
export function notificationsUrl(filter: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const key of ['mine', 'read', 'department', 'kind', 'days']) {
    const v = filter[key];
    if (typeof v === 'string' && v !== '') params.set(key, v);
  }
  const query = params.toString();
  return query ? `/notifications?${query}` : '/notifications';
}

/** Dokąd prowadzi kafelek — ekran z filtrem z odpowiedzi API. */
export function tileLink(tile: Pick<DashboardTile, 'target' | 'filter'>): string {
  if (tile.target === 'fleet') return fleetScheduleUrl(tile.filter);
  if (tile.target === 'notifications') return notificationsUrl(tile.filter);
  return buildListUrl('user-offers', tile.filter);
}

export interface TileText {
  label: string;
  description: string;
  /** Wyróżnienie, gdy liczba > 0. */
  highlight?: 'uwaga' | 'ostrzezenie';
}

/** Napisy kafelków — klucze jak w API (`ftl-dashboards.ts`). */
export const TILE_TEXT: Record<string, TileText> = {
  // Pulpit planisty
  myVariantsActive: {
    label: 'Moje warianty',
    description: 'czekają na decyzję Team Leadera',
  },
  myVariantsExpiringToday: {
    label: 'Termin mija dziś',
    description: 'moje warianty z terminem ważności do końca dnia',
    highlight: 'uwaga',
  },
  myVariantsExpired: {
    label: 'Po terminie',
    description: 'moje warianty po terminie — zwolnij albo zmień termin',
    highlight: 'ostrzezenie',
  },
  myVariantsConflict: {
    label: 'W konflikcie',
    description: 'mój pojazd jest w tym czasie wariantem albo obsadzeniem innego zlecenia',
    highlight: 'ostrzezenie',
  },
  toPlanInMyRegion: {
    label: 'Do obsadzenia w moim regionie',
    description: 'załadunek tam, gdzie zwalniają się moje pojazdy',
  },
  myCarsFree: {
    label: 'Wolne teraz',
    description: 'moje pojazdy bez obsadzenia w tej chwili',
  },
  myCarsBusy: {
    label: 'Zajęte teraz',
    description: 'moje pojazdy w trakcie obsadzenia',
  },
  myCarsNoPosition: {
    label: 'Bez pozycji',
    description: 'nie wiemy, gdzie się zwolnią — brak GPS i rozładunku',
    highlight: 'uwaga',
  },
  myNotificationsUnread: {
    label: 'Moje powiadomienia',
    description: 'nieprzeczytane powiadomienia imienne',
    highlight: 'uwaga',
  },
  myUnassignRequests: {
    label: 'Moje wnioski o odpięcie',
    description: 'czekają na decyzję przełożonego',
  },
  // Pulpit Team Leadera
  toAssignWithVariants: {
    label: 'Do obsadzenia z wariantami',
    description: 'planiści wskazali pojazdy — czeka na Twoją decyzję',
    highlight: 'uwaga',
  },
  toAssignWithoutVariants: {
    label: 'Do obsadzenia bez wariantu',
    description: 'żaden planista nie wskazał jeszcze pojazdu',
    highlight: 'ostrzezenie',
  },
  variantConflicts: {
    label: 'Konflikty wariantów',
    description: 'ten sam pojazd w nachodzącym czasie przy innym zleceniu',
    highlight: 'ostrzezenie',
  },
  variantsExpired: {
    label: 'Warianty po terminie',
    description: 'zwalnia autor albo Team Leader — nic nie dzieje się samo',
    highlight: 'uwaga',
  },
  unassignRequestsPending: {
    label: 'Wnioski o odpięcie',
    description: 'czekają na Twoją decyzję',
    highlight: 'ostrzezenie',
  },
  // Wnioski po wyjeździe auta (makieta back office v2, B6, 21.10.2026).
  changeRequestsPending: {
    label: 'Wnioski o zmianę pojazdu i przekazanie',
    description: 'po wyjeździe pojazdu na załadunek — czekają na Twoją decyzję',
    highlight: 'ostrzezenie',
  },
  lostVehicle: {
    label: 'Straciły pojazd',
    description: 'było obsadzone, a pojazd odpięto',
    highlight: 'ostrzezenie',
  },
  loadingTomorrow: {
    label: 'Jutro załadunek, bez auta',
    description: 'alert z przeglądu o 15:00 dzień przed załadunkiem',
    highlight: 'ostrzezenie',
  },
  replanAlerts: {
    label: 'Alerty przeplanowania',
    description: 'rekomendowane przeplanowanie czeka na potwierdzenie',
    highlight: 'uwaga',
  },
  inFtl: {
    label: 'W FTL',
    description: 'zlecenia w widoku działu FTL',
  },
  inTrade: {
    label: 'W Trade',
    description: 'zlecenia w widoku działu Trade',
  },
  handedToTradeToday: {
    label: 'Przekazane dziś do Trade',
    description: 'z FTL do Trade od początku dnia',
  },
};

/** Tytuły sekcji — kolejność jak w API. */
export const GROUP_TITLES: Record<string, string> = {
  variants: 'Warianty',
  orders: 'Zlecenia',
  fleet: 'Moje pojazdy',
  personal: 'Powiadomienia i wnioski',
  toAssign: 'Do obsadzenia',
  attention: 'Wymaga uwagi',
  balance: 'Bilans FTL / Trade',
};

/** Sekcja „Warianty” pulpitu planisty mówi o moich wariantach. */
export const PLANNER_GROUP_TITLES: Record<string, string> = {
  ...GROUP_TITLES,
  variants: 'Moje warianty',
};

/** Kafelki w sekcjach, w kolejności z odpowiedzi API. */
export function groupTiles(tiles: readonly DashboardTile[]): Array<[string, DashboardTile[]]> {
  const groups: Array<[string, DashboardTile[]]> = [];
  for (const t of tiles) {
    const last = groups.find(([g]) => g === t.group);
    if (last) last[1].push(t);
    else groups.push([t.group, [t]]);
  }
  return groups;
}

/** „Załadunek w ciągu 2 dni” — horyzont z parametru, słowem. */
export function horizonText(days: number): string {
  return days === 1 ? '1 dnia' : `${days} dni`;
}

export async function fetchPlannerDashboard(): Promise<PlannerDashboardData> {
  const { data } = await axiosNest.get<PlannerDashboardData>('/ftl-dashboards/planner');
  return {
    tiles: Array.isArray(data?.tiles) ? data.tiles : [],
    caretaker: data?.caretaker ?? null,
    vehicleCount: data?.vehicleCount ?? null,
    regions: Array.isArray(data?.regions) ? data.regions : [],
    horizonDays: data?.horizonDays ?? 2,
    regionPostalDigits: data?.regionPostalDigits ?? 0,
    ...(data?.regionPostalDigitsByCountry && typeof data.regionPostalDigitsByCountry === 'object'
      ? { regionPostalDigitsByCountry: data.regionPostalDigitsByCountry }
      : {}),
    computedAt: data?.computedAt ?? '',
  };
}

export async function fetchTeamLeaderDashboard(): Promise<TeamLeaderDashboardData> {
  const { data } = await axiosNest.get<TeamLeaderDashboardData>('/ftl-dashboards/team-leader');
  return {
    tiles: Array.isArray(data?.tiles) ? data.tiles : [],
    horizonDays: data?.horizonDays ?? 2,
    computedAt: data?.computedAt ?? '',
  };
}


// ── Bilans floty i postęp planistów, deficyt i nadwyżka (S-24.10-08) ──

/** Dokąd prowadzi liczba bilansu — ekran z filtrem z odpowiedzi API. */
export interface BalanceLink {
  target: 'orders' | 'fleet';
  filter: Record<string, unknown>;
}

export interface FleetBalanceRow {
  /** `null` — auta bez opiekuna (albo wiersz całej floty, `total`). */
  caretaker: string | null;
  total: boolean;
  vehicles: number;
  /** Wolne teraz, za 6, 12 i 24 h. */
  freeAt: number[];
  plannedBeyondTomorrow: number;
  /** `null` — nie policzono (lista bez filtra „Wymagające obsadzenia”). */
  variantsWaiting: number | null;
  links: {
    vehicles: BalanceLink | null;
    freeNow: BalanceLink | null;
    later: BalanceLink | null;
    variantsWaiting: BalanceLink | null;
  };
}

export type RegionBalanceKind = 'deficit' | 'surplus' | 'even';

export interface RegionBalanceRow {
  country: string;
  orders: number;
  vehicles: number;
  balance: RegionBalanceKind;
  ordersLink: BalanceLink;
}

export interface TeamLeaderBalanceData {
  fleet: FleetBalanceRow[];
  regions: {
    rows: RegionBalanceRow[];
    ordersWithoutCountry: number;
    vehiclesWithoutPlace: number;
  } | null;
  horizonHours: number;
  counterHours: number[];
  computedAt: string;
}

/** Chip przy kraju — słowem. */
export const BALANCE_LABELS: Record<RegionBalanceKind, string> = {
  deficit: 'deficyt',
  surplus: 'nadwyżka',
  even: 'równo',
};

/** Adres liczby bilansu — ten sam mechanizm co kafelki. */
export function balanceLink(link: BalanceLink | null): string | null {
  if (!link) return null;
  return link.target === 'fleet' ? fleetScheduleUrl(link.filter) : buildListUrl('user-offers', link.filter);
}

export async function fetchTeamLeaderBalance(): Promise<TeamLeaderBalanceData> {
  const { data } = await axiosNest.get<TeamLeaderBalanceData>('/ftl-dashboards/team-leader/balance');
  return {
    fleet: Array.isArray(data?.fleet) ? data.fleet : [],
    regions: data?.regions ?? null,
    horizonHours: data?.horizonHours ?? 48,
    counterHours: Array.isArray(data?.counterHours) ? data.counterHours : [0, 6, 12, 24],
    computedAt: data?.computedAt ?? '',
  };
}
